import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type { DataResponse, RegalStav, RegalUlozRequest, RegalVysledok } from "@/api/schemas";

/**
 * Konfigurátor regálu CRC (KOLO 232) — regál rozpočtu tovaru na otvorenie
 * v okne a Vložiť do rozpočtu s jedným krokom späť.
 */
export function useRegal(variantaId: string | null): UseQueryResult<RegalStav> {
  return useQuery({
    queryKey: ["regal", variantaId],
    queryFn: async () => {
      const { data } = await api.get<DataResponse<RegalStav>>(`/varianty/${variantaId}/regal`);
      return data.data;
    },
    enabled: Boolean(variantaId),
    staleTime: 0,
  });
}

function obnov(qc: ReturnType<typeof useQueryClient>, zakazkaId: string, variantaId: string): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
    qc.invalidateQueries({ queryKey: ["regal", variantaId] }),
    qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all }),
  ]);
}

export function useUlozRegal(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<RegalVysledok, Error, RegalUlozRequest> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (payload: RegalUlozRequest) => {
      const { data } = await api.put<DataResponse<RegalVysledok>>(
        `/varianty/${variantaId}/regal`,
        payload,
      );
      return data.data;
    },
    onSuccess: (vysledok) => {
      undo?.pridaj({
        popis: "vloženie regálu CRC",
        vrat: async () => {
          await api.post(`/varianty/${variantaId}/regal/vratit`, { predtym: vysledok.predtym });
          await obnov(qc, zakazkaId, variantaId);
        },
      });
      void obnov(qc, zakazkaId, variantaId);
    },
  });
}

/** Veta po vložení — počty, nenamapované diely a čo dostal rozpočtár. */
export function vetaVlozenia(v: RegalVysledok): string {
  const casti: string[] = [];
  const zmien = v.pridane + v.zmenene + v.zmazane;
  casti.push(
    zmien === 0
      ? "Regál je v rozpočte bez zmeny."
      : `Regál vložený: ${v.zostavy.length} ${v.zostavy.length === 1 ? "zostava" : v.zostavy.length < 5 ? "zostavy" : "zostáv"}` +
          ` (pribudlo ${v.pridane}, zmenilo sa ${v.zmenene}, odišlo ${v.zmazane}).`,
  );
  if (v.doplnit_cenu > 0) casti.push(`Doplň cenu ${v.doplnit_cenu} ${v.doplnit_cenu === 1 ? "položke" : "položkám"} (nákup 0).`);
  if (v.nenamapovane.length > 0) casti.push(`Mimo cenníka: ${v.nenamapovane.join(", ")}.`);
  if (v.tan) {
    casti.push(
      v.tan.aktualizovany
        ? `Diely Tandemu (${v.tan.dielcov} dielcov) — rozpočtár má import aktualizovaný.`
        : `Diely Tandemu (${v.tan.dielcov} dielcov) poslané rozpočtárovi na nacenenie do ${v.tan.varianta_nazov ?? "nábytku"}.`,
    );
  }
  return casti.join(" ");
}
